import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, ToggleRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { ResourceTable } from "@/components/data/ResourceTable";
import { RefSelect } from "@/components/data/FieldInput";
import { StatutDialog, PiecesJointes, EventList } from "@/components/data/Network";
import { PageHeader } from "@/components/data/StatusBadge";
import { api, errMsg, fmtDate, today } from "@/lib/api";

const TPE_FIELD = { name: "tpe_id", type: "ref", ref: "tpes" };

function AssignTpeDialog({ rev, onClose, onDone }) {
  const [f, setF] = useState({ tpe_id: null, date: today(), motif: "" });
  useEffect(() => {
    if (rev) setF({ tpe_id: null, date: today(), motif: "" });
  }, [rev]);
  const submit = async () => {
    try {
      await api.post(`/tpes/${f.tpe_id}/affecter`, { revendeur_id: rev.id, date: f.date, motif: f.motif });
      toast.success("TPE affecté");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!rev} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-tpe-dialog">
        <DialogHeader>
          <DialogTitle>Affecter un TPE à {rev?.code}</DialogTitle>
          <DialogDescription>Seuls les TPE actifs et non affectés sont proposés. Pour déplacer un TPE déjà affecté, utilisez « Réaffecter » dans le module TPE.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>TPE disponible</Label><div className="mt-1"><RefSelect field={TPE_FIELD} value={f.tpe_id} onChange={(v) => setF({ ...f, tpe_id: v })} filterFn={(o) => !o.revendeur_id && o.statut === "actif"} testId="assign-tpe-select" /></div></div>
          <div><Label>Date d'affectation</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          <div><Label>Motif / commentaire</Label><Textarea value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.tpe_id} data-testid="assign-tpe-submit">Affecter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevendeurFiche({ row }) {
  const [d, setD] = useState({ tpes: [], historique: [] });
  useEffect(() => {
    api.get(`/revendeurs/${row.id}/fiche`).then((r) => setD(r.data)).catch(() => {});
  }, [row.id]);
  return (
    <>
      <div className="mt-8" data-testid="revendeur-tpes">
        <h4 className="mb-3 text-xs font-bold uppercase tracking-wider">TPE affectés ({d.tpes.length})</h4>
        <div className="border rounded-md divide-y text-xs">
          {d.tpes.map((t) => (
            <div key={t.id} className="p-2.5 flex justify-between"><span className="font-mono font-semibold">{t.identifiant} <span className="text-muted-foreground font-normal">({t.numero_serie})</span></span><span className="text-muted-foreground">depuis le {fmtDate(t.date_affectation)}</span></div>
          ))}
          {!d.tpes.length && <div className="p-3 text-muted-foreground">Aucun TPE affecté</div>}
        </div>
      </div>
      <PiecesJointes res="revendeurs" entityId={row.id} />
      <EventList rows={d.historique} />
    </>
  );
}

export default function RevendeursPage() {
  const { can } = useAuth();
  const [assign, setAssign] = useState(null);
  const [statut, setStatut] = useState(null);
  const [reload, setReload] = useState(0);
  const refresh = () => setReload((n) => n + 1);
  const extraActions = (row) => [
    { key: "assign-tpe", label: "Affecter un TPE", icon: Link2, show: can("tpes", "modifier") && row.statut === "actif", onClick: setAssign },
    { key: "statut", label: "Changer le statut", icon: ToggleRight, show: can("revendeurs", "modifier"), onClick: setStatut },
  ];
  return (
    <div>
      <PageHeader title="Revendeurs" subtitle="Fiches revendeurs : identité, pièces jointes, zone, superviseur et TPE affectés. Le superviseur doit couvrir la zone du revendeur." />
      <Tabs defaultValue="liste">
        <TabsList className="mb-4"><TabsTrigger value="liste" data-testid="tab-revendeurs-liste">Revendeurs</TabsTrigger><TabsTrigger value="historique" data-testid="tab-revendeurs-historique">Historique des rattachements</TabsTrigger></TabsList>
        <TabsContent value="liste">
          <ResourceTable res="revendeurs" extraActions={extraActions} reloadKey={reload} detailExtra={(row) => <RevendeurFiche row={row} />} />
        </TabsContent>
        <TabsContent value="historique"><ResourceTable res="historique_revendeurs" reloadKey={reload} /></TabsContent>
      </Tabs>
      <AssignTpeDialog rev={assign} onClose={() => setAssign(null)} onDone={refresh} />
      <StatutDialog res="revendeurs" row={statut} onClose={() => setStatut(null)} onDone={refresh} />
    </div>
  );
}
